/**
 * Desktop-navigation "shift the canvas down" rules.
 *
 * Ported from `scss/main.scss`'s `.main--nav-opened` block (the old, pre-dock
 * canvas markup). That block shifted `.canvas` down with `transform:
 * translateY(...)` by different amounts depending on which section of the
 * desktop nav was active, so the focused group wasn't hidden behind the open
 * menu. Under `USE_NEW_CANVAS` the real canvas lives outside `.main`
 * (VisualizerDockShell, next to `.base`), so that CSS can no longer reach it —
 * this file is the same lookup table, ported so VisualizerDockShell can apply
 * it itself instead of always shifting by one flat amount.
 *
 * How matching works:
 * Every rule in `scss/main.scss` compiled to a selector built purely out of
 * `.main--<name>` modifier classes (plus the shared `.main`/`.main--nav-opened`
 * and a `.canvas` descendant, which don't affect which rule wins). A React
 * page assembles those same modifier names from up to three places: a
 * hardcoded scene class ("kv_scene" / "bv_scene" / "bv_look", only added by
 * kvSceneVisualizer.js / bvSceneVisualizer.js / lookVisualizer.js),
 * `activeNavigationParentName`, and `activeNavigationChildName` (both from
 * `navigationState` in Redux) — see e.g. bathroomVisualizer.js's `className`.
 * CSS doesn't care which of those three supplied a given class, only which
 * classes are present together and how many of them a rule required (more
 * required classes = higher specificity = wins), so `NAV_SHIFT_RULES` below
 * matches the same way: each rule lists the modifier names it requires, and
 * `getNavShiftHeight` returns the *most specific* (most `requires`) matching
 * rule, with later entries winning ties — exactly like the CSS cascade the
 * rules came from.
 *
 * Two names are genuinely declared twice in the source stylesheet with equal
 * specificity, so the later declaration was already the only one that ever
 * took effect there; that's preserved here (both entries are kept, in the
 * same order, purely so this file reads as a 1:1 port):
 *   - bv_scene: `handle_shower_valve` (75px, then overridden to 100px)
 *               and `hand_shower_kit` (100px, then overridden to 125px)
 *   - bv_look: `shower_pod` (100px, then overridden to 250px)
 *              and `lamp` (250px, then overridden to 350px)
 * Also, `scss/main.scss` has `&main--ceiling_panels` (missing the leading
 * `.`/`&` dot) in the standalone ceiling rule — a selector that can never
 * match anything, so `.main--ceiling_panels` alone never actually shifted the
 * canvas in the old CSS. This port fixes that typo (see the `ceiling_panels`
 * rule below) so the fix isn't carried forward; flag it if that turns out to
 * be load-bearing somewhere.
 */

/** The three hardcoded "scene" modifier classes, keyed by `document.kvConfig.visualizerType`. */
export const SCENE_CLASS_BY_VISUALIZER_TYPE: Record<string, string> = {
  kvScene: "kv_scene",
  KitchenV5: "kv_scene",
  bvScene: "bv_scene",
  BathroomV5: "bv_scene",
  bvLook: "bv_look",
  BathroomV6: "bv_look",
};

export interface NavShiftRule {
  /** Every one of these `main--<name>` modifier classes must be active for this rule to match. */
  requires: string[];
  /** px to translateY the canvas down by when this rule is the most specific match. */
  height: number;
}

/**
 * Listed in the exact order the corresponding rules appear in
 * `scss/main.scss`'s `.main--nav-opened` block, top to bottom.
 */
export const NAV_SHIFT_RULES: NavShiftRule[] = [
  // &.main--walls
  { requires: ["walls", "ceiling"], height: 300 },
  { requires: ["walls", "walls_ceiling"], height: 300 },
  { requires: ["walls", "ceiling_panels"], height: 300 },
  { requires: ["walls", "right_wall"], height: 100 },
  { requires: ["walls", "popout_wall"], height: 100 },
  { requires: ["walls", "left_wall"], height: 100 },
  { requires: ["walls", "front_wall"], height: 100 },
  { requires: ["walls", "center_wall"], height: 100 },

  // &.main--cabinet (plain KV, no scene)
  { requires: ["cabinet", "crown_molding"], height: 150 },
  { requires: ["cabinet", "top_cabinet"], height: 75 },

  // &.main--kv_scene &.main--cabinet
  { requires: ["kv_scene", "cabinet", "crown_molding"], height: 270 },
  { requires: ["kv_scene", "cabinet", "top_cabinet"], height: 200 },
  { requires: ["kv_scene", "cabinet", "top_handle"], height: 200 },
  { requires: ["kv_scene", "cabinet", "door_handle"], height: 200 },
  // &.main--kv_scene (no cabinet requirement)
  { requires: ["kv_scene", "hood"], height: 250 },
  { requires: ["kv_scene", "center_island_hood"], height: 250 },
  { requires: ["kv_scene", "center_island_pendants"], height: 250 },
  { requires: ["kv_scene", "microwave"], height: 250 },

  // &.main--ceiling, &main--ceiling_panels (standalone, no scene/parent)
  { requires: ["ceiling"], height: 300 },
  { requires: ["ceiling_panels"], height: 300 }, // typo-fixed, see file header

  // &.main--bv_scene
  { requires: ["bv_scene", "window"], height: 250 },
  { requires: ["bv_scene", "window_kits"], height: 250 },
  { requires: ["bv_scene", "window_body"], height: 250 },
  { requires: ["bv_scene", "handle_shower_valve"], height: 75 }, // overridden below, see file header
  { requires: ["bv_scene", "shower_pod"], height: 100 },
  { requires: ["bv_scene", "corner_shelf"], height: 100 },
  { requires: ["bv_scene", "handle_shower_valve"], height: 100 }, // wins (equal specificity, later in source)
  { requires: ["bv_scene", "hand_shower_kit"], height: 100 }, // overridden below, see file header
  { requires: ["bv_scene", "hand_shower_kit"], height: 125 }, // wins (equal specificity, later in source)
  { requires: ["bv_scene", "shower_fixture"], height: 250 },
  { requires: ["bv_scene", "shower_head"], height: 250 },
  { requires: ["bv_scene", "shower_arm"], height: 250 },

  // &.main--bv_look
  { requires: ["bv_look", "shower_pod"], height: 100 }, // overridden below, see file header
  { requires: ["bv_look", "corner_shelf"], height: 100 },
  { requires: ["bv_look", "wall_union"], height: 100 },
  { requires: ["bv_look", "valve_trim"], height: 100 },
  { requires: ["bv_look", "diverter_trim"], height: 100 },
  { requires: ["bv_look", "shower_door"], height: 100 },
  { requires: ["bv_look", "shower_wall"], height: 100 },
  { requires: ["bv_look", "grab_bar"], height: 175 },
  { requires: ["bv_look", "robe_hook"], height: 200 },
  { requires: ["bv_look", "shower_fixture"], height: 250 },
  { requires: ["bv_look", "hand_shower_holder"], height: 250 },
  { requires: ["bv_look", "hand_shower_only"], height: 250 },
  { requires: ["bv_look", "shower_hose"], height: 250 },
  { requires: ["bv_look", "shower_rod"], height: 250 },
  { requires: ["bv_look", "shower_pod"], height: 250 }, // wins (equal specificity, later in source)
  { requires: ["bv_look", "lamp"], height: 250 }, // overridden below, see file header
  { requires: ["bv_look", "vanity_mirror"], height: 250 },
  { requires: ["bv_look", "shower_head"], height: 300 },
  { requires: ["bv_look", "shower_arm"], height: 300 },
  { requires: ["bv_look", "lamp"], height: 350 }, // wins (equal specificity, later in source)
  { requires: ["bv_look", "vanity_lighting"], height: 350 },
];

/**
 * Returns the shift (px) for whichever `NAV_SHIFT_RULES` entry is the most
 * specific match against `activeModifiers` (i.e. has the most `requires`
 * entries, all present in `activeModifiers`), or 0 if none match. Ties go to
 * the later entry in `NAV_SHIFT_RULES`, matching CSS cascade order for
 * equal-specificity selectors.
 */
export function getNavShiftHeight(activeModifiers: Set<string> | string[]): number {
  const active = activeModifiers instanceof Set ? activeModifiers : new Set(activeModifiers);
  let best: NavShiftRule | null = null;
  for (const rule of NAV_SHIFT_RULES) {
    if (rule.requires.every((modifier) => active.has(modifier))) {
      if (!best || rule.requires.length >= best.requires.length) {
        best = rule;
      }
    }
  }
  return best ? best.height : 0;
}

export interface DesktopNavShiftState {
  desktopNavigationOpened: boolean;
  activeNavigationParentName?: string | null;
  activeNavigationChildName?: string | null;
  /** `document.kvConfig.visualizerType`, e.g. "kvScene" / "bvScene" / "bvLook" / "Kitchen" / ... */
  visualizerType?: string | null;
}

/**
 * Convenience wrapper around `getNavShiftHeight` that takes the raw
 * Redux/config values directly, the way `VisualizerDockShell` has them on
 * hand, and returns 0 whenever the desktop nav isn't open at all.
 */
export function getDesktopNavShiftHeight(state: DesktopNavShiftState): number {
  if (!state.desktopNavigationOpened) return 0;

  const sceneClass = state.visualizerType ? SCENE_CLASS_BY_VISUALIZER_TYPE[state.visualizerType] : undefined;
  const activeModifiers = [sceneClass, state.activeNavigationParentName, state.activeNavigationChildName].filter(
    (modifier): modifier is string => Boolean(modifier)
  );

  return getNavShiftHeight(activeModifiers);
}
